<template>
    <BaseModal
        v-if="show"
        :title="isEditing ? 'Edit Semester' : 'Add New Semester'"
        :loading="loading"
        :submit-text="isEditing ? 'Update' : 'Create'"
        max-width="2xl"
        @close="$emit('close')"
        @submit="handleSubmit"
    >
        <form @submit.prevent="handleSubmit" class="space-y-4">
            <!-- Academic Year Selection -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Academic Year
                </label>
                <SearchableSelect
                    v-model="form.academic_year_id"
                    :options="academicYears"
                    :optionLabel="(y) => y.name"
                    optionValue="id"
                    placeholder="Select Academic Year"
                    :error="errors.academic_year_id"
                />
            </div>

            <!-- Semester Name -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Semester Name
                </label>
                <input
                    v-model="form.name"
                    type="text"
                    required
                    placeholder="e.g., Odd, Even"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
                <div v-if="errors.name" class="text-red-500 text-sm mt-1">
                    {{ errors.name }}
                </div>
            </div>

            <!-- Start Date -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Start Date
                </label>
                <input
                    v-model="form.start_date"
                    type="date"
                    required
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
                <div v-if="errors.start_date" class="text-red-500 text-sm mt-1">
                    {{ errors.start_date }}
                </div>
            </div>

            <!-- End Date -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    End Date
                </label>
                <input
                    v-model="form.end_date"
                    type="date"
                    required
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
                <div v-if="errors.end_date" class="text-red-500 text-sm mt-1">
                    {{ errors.end_date }}
                </div>
            </div>

            <!-- Is Active -->
            <div class="flex items-center">
                <input
                    v-model="form.is_active"
                    type="checkbox"
                    id="is_active"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                    for="is_active"
                    class="ml-2 block text-sm text-gray-900 dark:text-gray-300"
                >
                    Set as active semester
                </label>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import BaseModal from "@/components/BaseModal.vue";
import SearchableSelect from "@/components/SearchableSelect.vue";
import type { Semester } from "@/api/semester";
import type { AcademicYear } from "@/api/academicYear";

interface Props {
    show: boolean;
    semester?: Semester | null;
    isEditing: boolean;
    academicYears: AcademicYear[];
}

import { useSemesterStore } from "@/stores/semester";

interface Emits {
    (e: "close"): void;
    (e: "saved"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const semesterStore = useSemesterStore();

const form = ref({
    academic_year_id: "",
    name: "",
    start_date: "",
    end_date: "",
    is_active: false,
});

const errors = ref<Record<string, string>>({});
const loading = ref(false);

// Initialize form when semester prop changes
watch(
    () => props.semester,
    (newSemester) => {
        if (newSemester) {
            form.value = {
                academic_year_id: newSemester.academic_year_id,
                name: newSemester.name,
                start_date: newSemester.start_date?.split("T")[0] ?? "",
                end_date: newSemester.end_date?.split("T")[0] ?? "",
                is_active: newSemester.is_active,
            };
        } else {
            form.value = {
                academic_year_id: "",
                name: "",
                start_date: "",
                end_date: "",
                is_active: false,
            };
        }
        errors.value = {};
    },
    { immediate: true },
);

const validateForm = () => {
    errors.value = {};

    if (!form.value.academic_year_id) {
        errors.value.academic_year_id = "Academic year is required";
    }

    if (!form.value.name.trim()) {
        errors.value.name = "Semester name is required";
    }

    if (!form.value.start_date) {
        errors.value.start_date = "Start date is required";
    }

    if (!form.value.end_date) {
        errors.value.end_date = "End date is required";
    }

    if (form.value.start_date && form.value.end_date) {
        if (new Date(form.value.end_date) <= new Date(form.value.start_date)) {
            errors.value.end_date = "End date must be after start date";
        }
    }

    return Object.keys(errors.value).length === 0;
};

const resetForm = () => {
    form.value = {
        academic_year_id: "",
        name: "",
        start_date: "",
        end_date: "",
        is_active: false,
    };
    errors.value = {};
};

const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    loading.value = true;
    errors.value = {};
    try {
        if (props.isEditing && props.semester) {
            await semesterStore.updateSemester(props.semester.id, {
                ...form.value,
            });
        } else {
            await semesterStore.createSemester({ ...form.value });
        }
        resetForm();
        emit("saved");
    } catch (error: any) {
        if (error.response?.status === 422) {
            const apiErrors = error.response.data.errors;
            for (const [key, messages] of Object.entries(apiErrors)) {
                errors.value[key] = Array.isArray(messages)
                    ? (messages as string[])[0]
                    : String(messages);
            }
            return;
        }
        console.error("Form submission error:", error);
    } finally {
        loading.value = false;
    }
};
</script>
